import { css } from "@compiled/react";
import { DEFAULT_VALUES, useLocalizedHref } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { Image } from "@/components/wpl/image/src";

const blogLinkStyles = css({
    margin: `${token("space.200")} ${token("space.200")} ${token("space.400")}`,
    fontSize: `${token("space.200")}`,
    img: {
        marginBlockEnd: `${token("space.100")}`
    }
});

const blogSectionStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: `${token("space.600")} ${token("space.200")} 36px`,
    backgroundColor: "#ffffff"
});

const flexWrap = css({
    display: "flex",
    [media.below.sm]: {
        flexWrap: "wrap"
    }
});

const blogLinkTextStyles = css({
    "&:link, &:visited": {
        color: "#0052cc",
        textDecoration: "none"
    },
    "&:hover": {
        "color": "#0065ff",
        ".link-arrow": {
            transition: "margin-left 0.25s ease-in-out",
            marginInlineStart: token("space.050")
        },
        ".link-text": {
            textDecoration: "underline"
        }
    }
});

const BlogsSection = () => {
    const intl = useIntl();

    return (
        <section css={blogSectionStyles}>
            <div
                css={css({
                    display: "flex",
                    maxWidth: "1440px",
                    [media.below.md]: {
                        display: "block"
                    }
                })}
            >
                <div
                    css={css({
                        [media.only.sm]: {
                            display: "grid",
                            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)"
                        },
                        [media.below.sm]: {
                            gridTemplateColumns: "minmax(0, 1fr)"
                        }
                    })}
                >
                    <div
                        css={css({
                            width: "58.3%",
                            padding: token("space.200"),
                            [media.below.md]: {
                                width: "90%",
                                paddingInlineEnd: "0"
                            },

                            [media.above.md]: {
                                transform: "translate3d(-20%, 0, 0)"
                            }
                        })}
                    >
                        <Image
                            src="https://wac-cdn.atlassian.com/dam/jcr:aa8f5b1f-b8ac-4d6e-9e7c-abe573b68164/number-one.svg?cdnVersion=1198"
                            alt="#1"
                        />
                    </div>
                    <div
                        css={css({
                            marginInlineStart: "16.6%",
                            padding: token("space.200"),
                            [media.above.md]: {
                                width: "66%"
                            },
                            [media.below.md]: {
                                marginInlineStart: "0"
                            },
                            h2: {
                                fontSize: "2.3rem",
                                color: "#344563",
                                [media.below.sm]: {
                                    fontSize: "1.5rem"
                                }
                            },
                            p: {
                                fontSize: "1rem",
                                fontWeight: "normal",
                                color: "#344563"
                            }
                        })}
                    >
                        <ContentBlock
                            headerType="h2"
                            headerText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.headerText",
                                defaultMessage:
                                    "Why Jira is the #1 tool recommended by agile teams.*"
                            })}
                            bodyText={
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.blogsSection.bodyText"
                                    defaultMessage="Trusted by more than 100,000 organizations, Jira Software comes out-of-the-box with the features and best practices agile teams need to develop and evolve their agile practices."
                                    values={DEFAULT_VALUES}
                                />
                            }
                        />
                        <div
                            css={css({
                                p: {
                                    fontSize: "0.9rem",
                                    color: "#42526e",
                                    margin: 0
                                }
                            })}
                        >
                            <p>
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.blogsSection.contentFooter"
                                    defaultMessage="*2021 State of Agile Report"
                                    values={DEFAULT_VALUES}
                                />
                            </p>
                        </div>
                    </div>
                </div>
                <div>
                    <div css={flexWrap}>
                        <BlogLink
                            imageSrc="https://wac-cdn.atlassian.com/dam/jcr:c30fba51-a9ab-4815-ad95-83e3f7582349/resource-agile.png?cdnVersion=1198"
                            imageAlt={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.agileAltText",
                                defaultMessage: "Agile illustration"
                            })}
                            href={useLocalizedHref("/agile")}
                            linkText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.agileLinkText",
                                defaultMessage: "Learn agile best practices"
                            })}
                        />
                        <BlogLink
                            imageSrc="https://wac-cdn.atlassian.com/misc-assets/webp-images/resource-playbook.webp"
                            imageAlt={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.teamPlaybookAltText",
                                defaultMessage: "Team playbook illustration"
                            })}
                            href={useLocalizedHref("/team-playbook")}
                            linkText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.teamPlaybookLinkText",
                                defaultMessage: "Team Playbook"
                            })}
                        />
                    </div>
                    <div css={flexWrap}>
                        <BlogLink
                            imageSrc="https://wac-cdn.atlassian.com/misc-assets/webp-images/resource-community.webp"
                            imageAlt={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.communityAltText",
                                defaultMessage: "Community illustration"
                            })}
                            href="https://community.atlassian.com/"
                            linkText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.communityLinkText",
                                defaultMessage: "Jira Software community"
                            })}
                        />
                        <BlogLink
                            imageSrc="https://wac-cdn.atlassian.com/dam/jcr:1de028c2-f441-40e9-b3ee-721e35e4a649/resource-devops.png?cdnVersion=1198"
                            imageAlt={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.devOpsAltText",
                                defaultMessage: "DevOps loop illustration"
                            })}
                            href={useLocalizedHref("/devops")}
                            linkText={intl.formatMessage({
                                id: "JiraSoftwarePageTemplate.blogsSection.devOpsLinkText",
                                defaultMessage: "Learn DevOps best practices"
                            })}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

const BlogLink = (props: {
    imageSrc: string;
    imageAlt: string;
    href: string;
    linkText: string;
}) => {
    return (
        <div css={blogLinkStyles}>
            <a
                href={props.href}
                data-event-component="linkButton"
                data-event-container="blogsSection"
                data-event="clicked"
                data-label={props.linkText}
                data-product-key="jiraSoftware"
            >
                <Image
                    src={props.imageSrc}
                    alt={props.imageAlt}
                    aspectRatio="16/9"
                />
            </a>
            {blogLinkText(props.href, props.linkText)}
        </div>
    );
};

const blogLinkText = (href: string, linkText: string) => {
    return (
        <div>
            <a
                href={href}
                css={blogLinkTextStyles}
                data-event-component="linkButton"
                data-event-container="blogsSection"
                data-event="clicked"
                data-label={linkText}
                data-product-key="jiraSoftware"
            >
                <div
                    className="link-text"
                    css={css({
                        display: "inline-flex",
                        fontSize: token("space.200")
                    })}
                >
                    {linkText}
                    <div className="link-arrow">{linkArrow()}</div>
                </div>
            </a>
        </div>
    );
};

const linkArrow = () => {
    return (
        <svg
            css={css({
                marginInlineStart: token("space.075")
            })}
            fill="currentColor"
            width="11"
            height="8"
            viewBox="0 0 11 8"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.40796 0L6.32831 1.2021L8.07854 3.1497H0.763182C0.341906 3.1497 0 3.53038 0 3.99943C0 4.46849 0.341906 4.84917 0.763182 4.84917H8.07854L6.32831 6.7979L7.40796 8L11 3.99943L7.40796 0Z"
            />
        </svg>
    );
};

export default BlogsSection;
